"use client"

import { ProjectPreview, ProjectPreviewSkeleton } from "./ProjectPreview"
import { Spinner, Text } from "@/elements"
import { FragmentType, graphql, useFragment } from "@/gql"
import { PortfolioStaticDataQuery } from "@/gql/graphql"
import type { NovaSession } from "@/types"
import { ApolloQueryResult, FetchMoreOptions, FetchMoreQueryOptions } from "@apollo/client"
import { ExclamationCircleIcon } from "@heroicons/react/24/outline"
import { useEffect, useRef } from "react"
import { usePortfolioProjects } from "../hooks/usePortfolioProjects"
import { ReorderProjectsFloatingCTA } from "./edit/ReorderProjectsFloatingCTA"
import { PortfolioUIState } from "../page"
import { useEditPortfolio } from "./edit/useEditPortfolio"

const PortfolioProjectsFragment = graphql(`
  fragment PortfolioProjects on Creative {
    id
    creativeWorkPortfolioProjects(
      first: $first
      after: $after
      status: PUBLISHED
      orderBy: "profileOrder"
      creatorRoles: $creatorRoles
      category: $categories
    ) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        cursor
        node {
          id
          category {
            id
          }
          creatorRoles {
            edges {
              node {
                id
              }
            }
          }
          ...ProjectPreview
        }
      }
    }
  }
`)

graphql(`
  query GetProjectsFromServer($handle: String!, $first: Int, $after: String, $creatorRoles: [ID!], $categories: [ID!]) {
    creativeByHandle(handle: $handle) {
      id
      creativeWorkPortfolioProjects(
        first: $first
        after: $after
        status: PUBLISHED
        orderBy: "profileOrder"
        creatorRoles: $creatorRoles
        category: $categories
      ) {
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          cursor
          node {
            id
            ...ProjectPreview
          }
        }
      }
    }
  }
`)

interface Props {
  serverCreative: FragmentType<typeof PortfolioProjectsFragment> | null | undefined
  clientCreative: FragmentType<typeof PortfolioProjectsFragment> | null | undefined
  handle: string
  uiState: PortfolioUIState
  staticData?: PortfolioStaticDataQuery | null | undefined
  fetchMoreClientSide?: (
    fetchMoreOptions: FetchMoreQueryOptions<any, any> & FetchMoreOptions<any, any>
  ) => Promise<ApolloQueryResult<any>>
  serverSession: NovaSession | null
  isFetchingFreshClientSideData: boolean
  editProfileMethods?: ReturnType<typeof useEditPortfolio>
}

export const PortfolioProjects: React.FC<Props> = props => {
  const {
    handle,
    staticData,
    uiState,
    serverSession,
    fetchMoreClientSide,
    isFetchingFreshClientSideData,
    editProfileMethods,
  } = props
  const clientCreative = useFragment(PortfolioProjectsFragment, props.clientCreative)
  const serverCreative = useFragment(PortfolioProjectsFragment, props.serverCreative)
  const shouldUseClientQueries = Boolean(serverSession?.user?.token)
  const { handleFetchMore, loading, loadingNextPage, projects } = usePortfolioProjects({
    shouldUseClientQueries,
    handle,
    fetchMoreClientSide,
    serverCreative,
    clientCreative,
    isFetchingFreshClientSideData,
  })

  const loadMoreRef = useRef(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        const first = entries[0]
        if (first.isIntersecting) {
          handleFetchMore()
        }
      },
      { threshold: 1.0 }
    )

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current)
    }

    return () => {
      observer.disconnect()
    }
  }, [loadMoreRef, handleFetchMore])

  const hasProjects = staticData?.creativeByHandle?.creativeWorkPortfolioProjects?.pageInfo.hasNextPage

  const noProjectsFoundWithFiltersApplied =
    !loading && projects?.length === 0 && hasProjects && !isFetchingFreshClientSideData
  const showSpinner = (loadingNextPage && !!projects && projects?.length > 0) || isFetchingFreshClientSideData

  return (
    <>
      <div className="relative">
        {noProjectsFoundWithFiltersApplied ? (
          <div className="flex w-full flex-row justify-center px-4">
            <div className="mt-10 flex max-w-[450px] flex-row items-center justify-center rounded-md bg-bg-1 p-4">
              <ExclamationCircleIcon height={30} className="mr-3" />
              <Text>No projects found with the selected filters.</Text>
            </div>
          </div>
        ) : (
          <div className="grid w-full grid-cols-12 gap-2 md:gap-6">
            {loading
              ? Array.from({ length: 5 }).map((_, index) => (
                  <div className="relative col-span-6 pb-2 md:pb-0 xl:col-span-4" key={index}>
                    <ProjectPreviewSkeleton key={index} />
                  </div>
                ))
              : projects?.map((edge: any, index: number) => {
                  return (
                    <div className="relative col-span-6 pb-2 md:pb-0 xl:col-span-4" key={edge?.node?.id}>
                      {edge?.node && (
                        <ProjectPreview
                          project={edge.node}
                          priority={index < 9}
                          handle={handle}
                          editProfileMethods={editProfileMethods}
                        />
                      )}
                    </div>
                  )
                })}
          </div>
        )}
        <div className="relative my-5 flex h-[20px] w-full flex-row items-center justify-center" ref={loadMoreRef}>
          {showSpinner && <Spinner className="bg-fg-1" />}
        </div>
      </div>
      <ReorderProjectsFloatingCTA editProfileMethods={editProfileMethods} />
    </>
  )
}
